import React, { useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ProtectedRoute } from '../../components/auth/ProtectedRoute';
import { TenantRole, useTenantRoles } from '../../hooks/useTenants';
import { apiClient } from '../../services/api';
import toast from 'react-hot-toast';
import {
  ShieldCheckIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  UsersIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';

const TenantRoles: React.FC = () => {
  const { currentTenant, hasPermission } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRole, setEditingRole] = useState<TenantRole | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);

  // Form state
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  const canManageRoles = hasPermission('tenant.roles.manage') || hasPermission('tenant.update');

  // Roles come from the dedicated TenantRole endpoint; they already match the
  // generated TenantRole shape ({ ..., permissions: string[], isSystem }), so
  // no per-field remapping is needed.
  const { roles } = useTenantRoles(currentTenant?.id || '');

  // Available permissions grouped by category
  const permissionCategories = {
    'User Management': [
      { id: 'users.read', label: 'View Users', description: 'View user information' },
      { id: 'users.write', label: 'Manage Users', description: 'Create and edit users' },
      { id: 'users.delete', label: 'Delete Users', description: 'Remove users from system' },
      { id: 'tenant.users.view', label: 'View Tenant Users', description: 'View users in tenant' },
      { id: 'tenant.users.manage', label: 'Manage Tenant Users', description: 'Invite and manage tenant users' },
    ],
    'Tenant Management': [
      { id: 'tenant.read', label: 'View Tenant', description: 'View tenant information' },
      { id: 'tenant.update', label: 'Update Tenant', description: 'Modify tenant settings' },
      { id: 'tenant.roles.manage', label: 'Manage Roles', description: 'Create and modify roles' },
      { id: 'tenant.billing.view', label: 'View Billing', description: 'View billing information' },
      { id: 'tenant.billing.manage', label: 'Manage Billing', description: 'Update payment methods' },
    ],
    'Network Operations': [
      { id: 'network.monitor', label: 'Monitor Network', description: 'View network status' },
      { id: 'network.configure', label: 'Configure Network', description: 'Modify network settings' },
      { id: 'network.manage', label: 'Manage Network', description: 'Full network control' },
      { id: 'nodes.read', label: 'View Nodes', description: 'View node information' },
      { id: 'nodes.write', label: 'Manage Nodes', description: 'Create and modify nodes' },
    ],
    'Security': [
      { id: 'security.view', label: 'View Security', description: 'View security settings' },
      { id: 'security.configure', label: 'Configure Security', description: 'Modify security settings' },
      { id: 'security.audit', label: 'Audit Access', description: 'View audit logs' },
      { id: 'alerts.view', label: 'View Alerts', description: 'View security alerts' },
      { id: 'alerts.manage', label: 'Manage Alerts', description: 'Configure alert rules' },
    ],
    'Storage & Data': [
      { id: 'storage.read', label: 'View Storage', description: 'View storage information' },
      { id: 'storage.write', label: 'Manage Storage', description: 'Modify storage settings' },
      { id: 'consensus.view', label: 'View Consensus', description: 'View consensus data' },
      { id: 'consensus.participate', label: 'Participate in Consensus', description: 'Vote and participate' },
    ],
  };

  const togglePermission = (permission: string) => {
    setSelectedPermissions(prev =>
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  const handleCreateRole = async () => {
    if (!currentTenant) {
      toast.error('No tenant selected');
      return;
    }

    try {
      const response = await apiClient.createTenantRole(currentTenant.id, {
        name: roleName,
        description: roleDescription,
        permissions: selectedPermissions,
      });

      if (response.success) {
        toast.success(`Role "${roleName}" created successfully`);
        resetForm();
        setShowCreateModal(false);
        // Refresh tenant data to show new role
        window.location.reload();
      } else {
        toast.error(response.error || 'Failed to create role');
      }
    } catch (error) {
      toast.error('Failed to create role');
      console.error('Error creating role:', error);
    }
  };

  const handleUpdateRole = async () => {
    if (!editingRole || !currentTenant) {
      toast.error('No role selected or tenant unavailable');
      return;
    }

    try {
      const response = await apiClient.updateTenantRole(currentTenant.id, editingRole.id, {
        name: roleName,
        description: roleDescription,
        permissions: selectedPermissions,
      });

      if (response.success) {
        toast.success(`Role "${roleName}" updated successfully`);
        resetForm();
        setEditingRole(null);
        // Refresh tenant data to show updated role
        window.location.reload();
      } else {
        toast.error(response.error || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
      console.error('Error updating role:', error);
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!currentTenant) {
      toast.error('No tenant selected');
      return;
    }

    try {
      const response = await apiClient.deleteTenantRole(currentTenant.id, roleId);

      if (response.success) {
        toast.success('Role deleted successfully');
        setShowDeleteConfirm(null);
        // Refresh tenant data to remove deleted role
        window.location.reload();
      } else {
        toast.error(response.error || 'Failed to delete role');
      }
    } catch (error) {
      toast.error('Failed to delete role');
      console.error('Error deleting role:', error);
    }
  };

  const startEditRole = (role: TenantRole) => {
    setEditingRole(role);
    setRoleName(role.name);
    setRoleDescription(role.description || '');
    setSelectedPermissions(role.permissions);
  };

  const resetForm = () => {
    setRoleName('');
    setRoleDescription('');
    setSelectedPermissions([]);
    setEditingRole(null);
  };

  const getRoleUserCount = (role: TenantRole): number => {
    // In a real implementation, this would count actual users
    return Math.floor(Math.random() * 20) + 1;
  };

  const isSystemRole = (role: TenantRole): boolean => {
    return ['owner', 'admin', 'member', 'viewer'].includes(role.name.toLowerCase());
  };

  return (
    <ProtectedRoute requiredPermission="tenant.roles.manage">
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Tenant Roles</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  Manage roles and permissions for {currentTenant?.name}
                </p>
              </div>
              {canManageRoles && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create Role
                </button>
              )}
            </div>
          </div>

          {/* Roles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roles.map(role => {
              const userCount = getRoleUserCount(role);
              const isSystem = isSystemRole(role);
              
              return (
                <div
                  key={role.id}
                  className="bg-card rounded-lg border border-border p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center">
                      <ShieldCheckIcon className="h-8 w-8 text-primary mr-3" />
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">{role.name}</h3>
                        {isSystem && (
                          <span className="text-xs text-muted-foreground bg-accent px-2 py-0.5 rounded-full">
                            System Role
                          </span>
                        )}
                      </div>
                    </div>
                    {canManageRoles && !isSystem && (
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => startEditRole(role)}
                          className="p-1 rounded hover:bg-accent transition-colors"
                        >
                          <PencilIcon className="h-4 w-4 text-muted-foreground" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(role.id)}
                          className="p-1 rounded hover:bg-accent transition-colors"
                        >
                          <TrashIcon className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </div>
                    )}
                  </div>

                  <p className="text-sm text-muted-foreground mb-4">
                    {role.description || 'No description provided'}
                  </p>

                  <div className="space-y-3">
                    <div className="flex items-center text-sm">
                      <UsersIcon className="h-4 w-4 text-muted-foreground mr-2" />
                      <span className="text-foreground">{userCount} users</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <KeyIcon className="h-4 w-4 text-muted-foreground mr-2" />
                      <span className="text-foreground">{role.permissions.length} permissions</span>
                    </div>
                  </div>

                  <div className="mt-4">
                    <p className="text-xs text-muted-foreground mb-2">Permissions:</p>
                    <div className="flex flex-wrap gap-1">
                      {role.permissions.slice(0, 3).map((permission, idx) => (
                        <span
                          key={permission ?? idx}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary"
                        >
                          {permission}
                        </span>
                      ))}
                      {role.permissions.length > 3 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-accent text-muted-foreground">
                          +{role.permissions.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Empty State */}
          {roles.length === 0 && (
            <div className="text-center py-12">
              <ShieldCheckIcon className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-2 text-sm font-medium text-foreground">No roles defined</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Get started by creating your first role
              </p>
              {canManageRoles && (
                <div className="mt-6">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create Role
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Create/Edit Role Modal */}
        {(showCreateModal || editingRole) && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-card rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                {editingRole ? 'Edit Role' : 'Create New Role'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Role Name
                  </label>
                  <input
                    type="text"
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="e.g., Developer, Support Agent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Description
                  </label>
                  <textarea
                    value={roleDescription}
                    onChange={(e) => setRoleDescription(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Describe the purpose of this role"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Permissions
                  </label>
                  <div className="space-y-4">
                    {Object.entries(permissionCategories).map(([category, permissions]) => (
                      <div key={category}>
                        <h4 className="text-sm font-medium text-foreground mb-2">{category}</h4>
                        <div className="space-y-2 pl-4">
                          {permissions.map(permission => (
                            <label
                              key={permission.id}
                              className="flex items-start space-x-3 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedPermissions.includes(permission.id)}
                                onChange={() => togglePermission(permission.id)}
                                className="mt-0.5 rounded border-border text-primary focus:ring-primary"
                              />
                              <div className="flex-1">
                                <span className="text-sm text-foreground">{permission.label}</span>
                                <p className="text-xs text-muted-foreground">{permission.description}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    resetForm();
                    setShowCreateModal(false);
                  }}
                  className="px-4 py-2 bg-accent text-foreground rounded-lg hover:bg-accent/80 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={editingRole ? handleUpdateRole : handleCreateRole}
                  disabled={!roleName || selectedPermissions.length === 0}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingRole ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-card rounded-lg p-6 max-w-md w-full">
              <div className="flex items-center mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mr-3" />
                <h3 className="text-lg font-semibold text-foreground">Delete Role</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Are you sure you want to delete this role? All users with this role will need to be reassigned.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-4 py-2 bg-accent text-foreground rounded-lg hover:bg-accent/80 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteRole(showDeleteConfirm)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Role
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ProtectedRoute>
  );
};

export default TenantRoles;